<script setup lang="ts" generic="T">
import type { EmblaCarouselType } from 'embla-carousel'
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-light-svg-icons'
import autoscroll from 'embla-carousel-auto-scroll'
import autoplay from 'embla-carousel-autoplay'
import emblaCarouselVue from 'embla-carousel-vue'

const props = withDefaults(defineProps<{
  slidesToScroll?: number
  controls?: boolean
  variant?: 'nettbutikk' | 'default'
  loop?: boolean
  autoplay?: boolean
  autoscroll?: boolean
  data?: T[]
  centerCards?: boolean
  dots?: boolean
}>(), {
  slidesToScroll: 1,
  controls: false,
  loop: false,
  autoplay: false,
  autoscroll: false,
  centerCards: false,
  dots: false,
})

const options = computed(() => ({
  slidesToScroll: props.slidesToScroll,
  loop: props.loop || props.autoscroll || props.autoplay,
}))

const [emblaRef, api] = emblaCarouselVue(
  options,
  computed(() => {
    const plugins = []
    if (props.autoplay) {
      plugins.push(autoplay({ delay: 3000 }))
    }
    if (props.autoscroll) {
      plugins.push(autoscroll())
    }
    return plugins
  }),
)

const canScrollNext = ref(false)
const canScrollPrev = ref(false)
const snapCount = ref(0)
const selectedSnap = ref(0)

function onSelect(api: EmblaCarouselType) {
  canScrollNext.value = api.canScrollNext() || false
  canScrollPrev.value = api.canScrollPrev() || false
  snapCount.value = api.scrollSnapList().length
  selectedSnap.value = api.selectedScrollSnap()
}

const showDots = computed(() => props.dots && snapCount.value > 1)

onMounted(() => {
  api.value?.on('init', onSelect)
  api.value?.on('reInit', onSelect)
  api.value?.on('select', onSelect)

  if (api.value) {
    onSelect(api.value)
  }

  if (props.autoplay) {
    api.value?.plugins().autoplay?.play()
  }

  if (props.autoscroll) {
    api.value?.plugins().autoscroll?.play()
  }
})

onBeforeUnmount(() => {
  api.value?.off('init', onSelect)
  api.value?.off('reInit', onSelect)
  api.value?.off('select', onSelect)
})

const arrivedAttr = computed(() => {
  if (canScrollNext.value) {
    return canScrollPrev.value ? 'none' : 'left'
  }
  return canScrollPrev.value ? 'right' : 'both'
})

function scrollPrev() {
  api.value?.scrollPrev()
}

function scrollNext() {
  api.value?.scrollNext()
}

const prevButton = useTemplateRef<HTMLButtonElement>('prevButton')
const nextButton = useTemplateRef<HTMLButtonElement>('nextButton')

function handOverFocus(from: HTMLButtonElement | null, to: HTMLButtonElement | null) {
  if (from && to && document.activeElement === from) {
    to.focus()
  }
}

watch(canScrollPrev, (canScroll) => {
  if (!canScroll) {
    handOverFocus(prevButton.value, nextButton.value)
  }
})

watch(canScrollNext, (canScroll) => {
  if (!canScroll) {
    handOverFocus(nextButton.value, prevButton.value)
  }
})

const containerRef = useTemplateRef('container')

const { width } = useElementSize(containerRef)

const { width: windowWidth } = useWindowSize({ includeScrollbar: false })

const NETTBUTIKK_MIN_SLACK = 140

// Below 2xl the default variant overlays its chevrons on the track; from 2xl they sit outside it, in room the consumer's padding provides.
const hasRoomForControls = computed(() => {
  if (props.variant !== 'default') {
    return windowWidth.value - width.value >= NETTBUTIKK_MIN_SLACK
  }
  return true
})

const showControls = computed(() => hasRoomForControls.value && !!api.value && props.controls && (canScrollNext.value || canScrollPrev.value))

// Keeps both chevrons mounted across page changes so the template refs stay bound for
// handOverFocus; they are out of flow, so unmounting them on resize shifts nothing.
const layoutControls = computed(() => props.variant === 'default' ? props.controls && hasRoomForControls.value : showControls.value)

const attrs = useAttrs()

const announcement = computed(() => {
  if (!showDots.value) {
    return ''
  }
  const position = `Side ${selectedSnap.value + 1} av ${snapCount.value}`
  const label = attrs['aria-label']
  return label ? `${label}: ${position.toLowerCase()}` : position
})
</script>

<template>
  <div :class="{ 'flex flex-col gap-l': dots }" role="region" aria-roledescription="karusell">
    <div
      ref="container"
      :class="{
        'flex items-center': layoutControls,
        'relative': layoutControls && variant === 'default',
        'min-[1560px]:-mx-12.5': layoutControls && variant !== 'default',
        'gap-5': showControls && variant === 'nettbutikk',
      }"
    >
      <div
        v-if="layoutControls && variant === 'default'"
        aria-hidden="true"
        class="carousel-edge-backdrop left-0 shadow-[15px_0_15px_0_var(--surface-elevated)]"
        :class="{ invisible: !showControls || !canScrollPrev }"
      />

      <slot v-if="layoutControls" name="prev-button" :scroll-prev="scrollPrev" :can-scroll="canScrollPrev">
        <button
          ref="prevButton"
          class="carousel-button"
          :class="{
            invisible: !showControls || !canScrollPrev,
            'absolute top-1/2 -translate-y-1/2 left-0 ml-2xs 2xl:-left-12 z-30 bg-surface-elevated': variant === 'default',
          }"
          aria-label="Rull til venstre"
          @click="scrollPrev"
        >
          <Icon :icon="faChevronLeft" size="sm" />
        </button>
      </slot>

      <div
        ref="emblaRef"
        class="overflow-hidden [--bg-color:theme(colors.carbon.50)]"
        :class="{ 'flex-1': variant === 'default' }"
        :data-arrived="showControls || dots ? undefined : arrivedAttr"
      >
        <!-- The leading padding keeps the first slide clear of the overlaid left chevron until the reader scrolls. -->
        <div v-if="variant === 'default'" class="flex gap-m md:pl-12 2xl:pl-0" :class="{ 'items-center': centerCards }">
          <slot />
        </div>
        <div v-else class="flex -ml-[--gap]" :class="{ 'items-center': centerCards }">
          <div v-for="(item, i) in data" :key="i" data-item class="min-w-0 pl-[--gap]">
            <slot :item="item" :index="i" />
          </div>
        </div>
      </div>

      <div
        v-if="layoutControls && variant === 'default'"
        aria-hidden="true"
        class="carousel-edge-backdrop right-0 shadow-[-15px_0_15px_0_var(--surface-elevated)]"
        :class="{ invisible: !showControls || !canScrollNext }"
      />

      <slot v-if="layoutControls" name="next-button" :scroll-next="scrollNext" :can-scroll="canScrollNext">
        <button
          ref="nextButton"
          class="carousel-button"
          :class="{
            invisible: !showControls || !canScrollNext,
            'absolute top-1/2 -translate-y-1/2 right-0 mr-2xs 2xl:-right-12 z-30 bg-surface-elevated': variant === 'default',
          }"
          aria-label="Rull til høyre"
          @click="scrollNext"
        >
          <Icon :icon="faChevronRight" size="sm" />
        </button>
      </slot>
    </div>

    <div v-if="showDots" class="flex justify-center gap-2xs" aria-hidden="true">
      <span
        v-for="snap in snapCount"
        :key="snap"
        class="size-2.5 shrink-0 rounded-full transition-colors duration-300"
        :class="snap - 1 === selectedSnap ? 'bg-[--carousel-dot-active,theme(colors.blurple.200)]' : 'bg-[--carousel-dot,theme(colors.carbon.200)]'"
      />
    </div>

    <p v-if="dots" class="sr-only" aria-live="polite">
      {{ announcement }}
    </p>
  </div>
</template>

<style scoped lang="postcss">
.carousel-button {
  @apply hidden md:flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-300;
  @apply border-border-default hover:border-none border hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed;
  @apply focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blurple-400 focus-visible:ring-offset-2;
}

.carousel-edge-backdrop {
  @apply hidden md:block absolute inset-y-0 w-12 z-20 bg-surface-elevated pointer-events-none;
  @apply 2xl:opacity-0 transition-opacity duration-300;
}

[data-arrived] {
  --fade: 98px;
  --gradient-color: var(--bg-color, white);

  &[data-arrived='both'] {
    mask-image: none;
  }

  &[data-arrived='none'] {
    mask-image: linear-gradient(
      to right,
      transparent 0,
      var(--gradient-color) var(--fade),
      var(--gradient-color) calc(100% - var(--fade)),
      transparent 100%
    );
  }

  &[data-arrived='left'] {
    mask-image: linear-gradient(to right, var(--gradient-color) calc(100% - var(--fade)), transparent 100%);
  }

  &[data-arrived='right'] {
    mask-image: linear-gradient(to left, var(--gradient-color) calc(100% - var(--fade)), transparent 100%);
  }
}

[data-item] {
  flex: 0 0 var(--item-width, 100%);
}
</style>
